import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { cn, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { AdminOrganizationListItem, SetupStatus } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { CalendarIcon, OrganizationsIcon, TagIcon, TextIcon, WorkspacesIcon } from '@/components/layout/icons';
import { FilterBar, FilterPill, FilterPillGroup } from '@/components/filter-bar';
import { ListEmptyState, TestWorkspacesPill, WorkspaceLabel } from '@/components/test-workspaces';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { includeTestWorkspacesQuery } from '@/lib/test-workspaces/include-test-workspaces';
import { SEARCH_PARAM, readEnumParam, readEnumParams, useListParams } from '@/lib/list-view/use-list-params';
import { useWorkspaceFilter } from '@/lib/list-view/use-workspace-filter';

export const organizationsLoaderParams = [] as const;

export type OrganizationsLoaderData = {
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly columnPreference: ColumnPreference;
};

export async function organizationsLoader(): Promise<OrganizationsLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminOrganizations.list({ query: includeTestWorkspacesQuery() }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.organizations),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load organizations', { status: result.status });
  }

  return { organizations: result.body.data, columnPreference };
}

const TYPES: ReadonlyArray<AdminOrganizationListItem['type']> = ['main', 'subsidiary'];

// Exported so a test can prove it holds every SetupStatus.
export const SETUP_STATUS_ORDER = ['IN_SETUP', 'COMPLETED'] as const satisfies ReadonlyArray<SetupStatus>;

function matches(
  organization: AdminOrganizationListItem,
  query: string,
  selectedTypes: ReadonlyArray<AdminOrganizationListItem['type']>,
): boolean {
  if (selectedTypes.length > 0 && !selectedTypes.includes(organization.type)) return false;

  if (query === '') return true;
  const haystack = [organization.name, organization.workspaceName].join(' ').toLowerCase();

  return haystack.includes(query.toLowerCase());
}

function useOrganizationColumns(): ReadonlyArray<OperatorColumn<AdminOrganizationListItem>> {
  const t = useTranslations('pages.admin.organizations');
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        cell: (organization) => organization.name,
        sortValue: (organization) => organization.name,
      },
      {
        id: 'type',
        label: t('columns.type'),
        icon: TagIcon,
        cellClassName: 'text-text-tertiary',
        cell: (organization) => t(`types.${organization.type}`),
        sortValue: (organization) => t(`types.${organization.type}`),
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        cell: (organization) => (
          <WorkspaceLabel isTest={organization.isTest}>{organization.workspaceName}</WorkspaceLabel>
        ),
        sortValue: (organization) => organization.workspaceName,
      },
      {
        id: 'createdAt',
        label: t('columns.created-at'),
        icon: CalendarIcon,
        headClassName: 'w-[150px]',
        cellClassName: 'text-text-tertiary',
        cell: (organization) => intl.formatDate(organization.createdAt, { dateStyle: 'short' }),
        sortValue: (organization) => Date.parse(organization.createdAt),
      },
    ],
    [t, intl],
  );
}

export function OrganizationsPage() {
  const t = useTranslations('pages.admin.organizations');
  const tFilters = useTranslations('pages.admin.shell.filters');
  const { organizations, columnPreference } = useLoaderData() as OrganizationsLoaderData;
  const navigate = useNavigate();

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';
  const selectedTypes = readEnumParams(params, 'type', TYPES);
  const setupStatus = readEnumParam(params, 'setupStatus', SETUP_STATUS_ORDER);
  // An organisation row carries its workspace id and name itself; loader data is a stable reference.
  const workspace = useWorkspaceFilter(organizations);
  const serverEmpty = organizations.length === 0;
  const toggleType = (type: AdminOrganizationListItem['type']) => {
    const next = selectedTypes.includes(type) ? selectedTypes.filter((t) => t !== type) : [...selectedTypes, type];

    setParams({ type: next });
  };

  const filtered = useMemo(
    () =>
      organizations.filter(
        (o) =>
          (workspace.workspaceId === null || o.workspaceId === workspace.workspaceId) &&
          (setupStatus === null || o.setupStatus === setupStatus) &&
          matches(o, query.trim(), selectedTypes),
      ),
    [organizations, workspace.workspaceId, setupStatus, query, selectedTypes],
  );
  const catalogue = useOrganizationColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.organizations,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={OrganizationsIcon} />} />
      <FilterBar
        fixedPills={
          <>
            {TYPES.map((type) => (
              <FilterPill
                key={type}
                icon={TagIcon}
                label={t(`types.${type}`)}
                active={selectedTypes.includes(type)}
                onToggle={() => toggleType(type)}
              />
            ))}
            <FilterPillGroup
              label={t('setup-status.label')}
              allLabel={tFilters('all')}
              icon={TagIcon}
              options={SETUP_STATUS_ORDER.map((status) => ({ value: status, label: t(`setup-status.${status}`) }))}
              value={setupStatus}
              onChange={(next) => setParams({ setupStatus: next })}
            />
            <TestWorkspacesPill />
          </>
        }
        pills={workspace.pills}
        {...(workspace.pills !== null ? { overflow: workspace.overflow } : {})}
        searchValue={query}
        searchPlaceholder={tFilters('search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {filtered.length === 0 ? (
        <ListEmptyState title={serverEmpty ? t('empty') : t('empty-filtered')} serverEmpty={serverEmpty} />
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((organization) => (
              <TableRow
                key={organization.id}
                interactive
                onClick={() => navigate(`/workspaces/organizations/${organization.id}`)}
              >
                {table.columns.visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                    style={column.cellStyle}
                  >
                    {column.cell(organization)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
